/**
 * [ui1006, paczka KIT] REJESTR OBSZARÓW — „Procedury i bezpieczeństwo” (`/admin/procedury-bezpieczenstwo/…`).
 *
 * Jedno źródło nawigacji strony: 4 grupy, 16 obszarów (1 + 4 + 5 + 6) — rozdz. 4 projektu ui1006. Slug strony jest wspólny
 * dla PL i EN; tłumaczony jest tylko segment obszaru.
 *
 * [paczka PROC 2026-10-07] `aktywna: true` — starą stronę (trzy widoki w `?widok=`, okno procedury z dwiema belkami, kreator
 * w szufladzie, biblioteka materiałów na pełnym ekranie) zastąpiło 16 miejsc w `pages/polityki/obszary/*` (treść na
 * `useRamaObszaru()`); źródła startu stoją w `pages/polityki/zrodlaStartu.js` (4 odczyty), karta procedury
 * w `components/Polityki/karta/KartaProceduryObiekt.js`, kreator „Nowa procedura” w `components/Polityki/kreator/KreatorNowejProcedury.js`.
 * Klucze modułu i grupy części karty mieszkają w `kluczeProcedur.js` i `czesciProcedury.js` (czytają je też źródła startu
 * i karta — jedno źródło, bez cyklu importów rejestr ↔ strona).
 *
 * DWIE DECYZJE REJESTRU (projekt był tu niejednoznaczny — do potwierdzenia przez koordynatora):
 *  • filtr „Etap” listy procedur nazywa się `?filtrEtap=`, nie `?etap=`: `&etap=N` jest częścią adresu KARTY procedury
 *    (`?doc=ID&etap=N`, tak stoi w linkach powiadomień zapisanych w bazie), a jedna nazwa parametru ma na stronie jedno
 *    znaczenie (Z-05, reguła D-25: filtr o nazwie obiektu dostaje przedrostek `filtr`). Inaczej zamknięcie karty
 *    zostawiałoby listę zawężoną do etapu karty;
 *  • szuflada pozycji w „Różne nazwy stanowisk” dostaje parametr `?pisownia=ID` — tabela adresów projektu go nie podała,
 *    a każdy widok ma mieć adres (Z-05).
 *
 * DWIE POPRAWKI PO PRZEGLĄDZIE (2026-10-06) — obie zamrożone testem `config/obszary/__tests__/rejestry.test.js`:
 *  • klucze miejsc „Stanowiska” i „Różne nazwy stanowisk” są lustrem polityk serwera (opis przy stałych niżej);
 *  • miejsce ustawień nazywa się „Zatwierdzanie zarządu” (było: „Zatwierdzanie przez zarząd”) — rząd miejsc musi się
 *    zmieścić w 70rem (zasada Z-02c); limit jest twardy, więc ustępuje etykieta, nie limit.
 */
import {
    FiList, FiLayers, FiGrid, FiBriefcase, FiRefreshCw, FiCopy, FiImage, FiTag, FiUsers, FiShuffle,
    FiPlayCircle, FiBell, FiCheckCircle, FiAward, FiShield, FiSend,
} from "react-icons/fi";
import { GRUPA_PRACY, GRUPA_USTAWIEN, grupaStartu, grupaPracy, grupaUstawien, obszar, obszarStartu } from "./ksztalt";
import { KLUCZE_PROCEDUR as KLUCZE_MODULU, KLUCZE_USTAWIEN_PROCEDUR, KLUCZ_NAZW_STANOWISK, KLUCZ_RAPORTU_STANOWISK } from "./kluczeProcedur";
import GRUPY_CZESCI_PROCEDURY from "./czesciProcedury";
import ZRODLA_STARTU_PROCEDUR from "../../pages/polityki/zrodlaStartu";

const GRUPA_BIBLIOTEK = "biblioteki";

/** Pięć kluczy modułu — którykolwiek otwiera stronę (ranga administratora nic tu nie otwiera). */
export const KLUCZE_PROCEDUR = KLUCZE_MODULU;
/**
 * KLUCZE MIEJSC = polityka serwera na ODCZYCIE, z którego miejsce żyje (sprawdzone w kontrolerach 2026-10-06):
 *  • „Stanowiska” — `GET /polityki/stanowiska/raport` stoi za `PERM:PolitykiRaportPotwierdzen` (nazwiska i stanowiska);
 *  • „Różne nazwy stanowisk” — `GET /polityki/stanowiska/warianty` stoi za `PERM:PolitykiRedakcja`;
 *  • ustawienia — `GET /polityki/ustawienia` wpuszcza redakcję ALBO posiadacza klucza ustawień.
 * [poprawka po przeglądzie 2026-10-06] BYŁO: oba miejsca stanowisk za „którymkolwiek z dwóch” (tak dzisiejsza strona
 * wpuszcza do WSPÓLNEJ sekcji z dwiema zakładkami). Po rozdzieleniu na dwa miejsca klucz otwierałby miejsce, a serwer
 * odmawiał danych — człowiek z samą redakcją wchodziłby w „Stanowiska” tylko po to, żeby zobaczyć odmowę.
 */
const KLUCZE_USTAWIEN = KLUCZE_USTAWIEN_PROCEDUR;

/* Karta procedury — jedyny obiekt z GRUPAMI części (etapy 1–4 i „Dane i historia”): 18 części w 5 grupach po ≤ 4
   (`czesciProcedury.js`). Adres: `?doc=ID&etap=1..4|dane&czesc=<kod>`. */

/** Dawne kody etapów w adresie (`?etap=pilnowanie`) → numer etapu karty. */
const NUMER_ETAPU = { przygotowanie: "1", zatwierdzenie: "2", publikacja: "3", pilnowanie: "4", 1: "1", 2: "2", 3: "3", 4: "4", dane: "dane" };

/** Dawna sekcja ustawień (`?widok=ustawienia&sekcja=…`) → nowy obszar (+ zapytanie). Tabela 4.10 projektu. */
const SEKCJA_NA_OBSZAR = {
    automat: { obszar: "ustawienia-powiadomienia" },
    powiadomienia: { obszar: "ustawienia-powiadomienia" },
    potwierdzenia: { obszar: "ustawienia-potwierdzenia" },
    eskalacja: { obszar: "ustawienia-potwierdzenia" },
    zaleglosci: { obszar: "ustawienia-potwierdzenia" },
    zarzad: { obszar: "ustawienia-zarzad" },
    objeta_ustawa: { obszar: "ustawienia-spolki" },
    spolki: { obszar: "ustawienia-spolki" },
    publikacja: { obszar: "ustawienia-publikacja" },
    kontrole: { obszar: "ustawienia-publikacja" },
    stanowiska: { obszar: "stanowiska" },
    obszary: { obszar: "obszary", dopisz: { widok: "wlasne" } },
    kto: { obszar: "ustawienia-uruchomienie", dopisz: { pojecia: "kto-co-robi" } },
};

/** Zapytanie bez wskazanych parametrów (reszta przechodzi w całości — linki nie mogą zgubić niczego po drodze). */
const bez = (parametry, nazwy) => {
    const nowe = new URLSearchParams(parametry);
    nazwy.forEach((n) => nowe.delete(n));
    return nowe;
};

/**
 * STARE ADRESY (tabela 4.10 projektu) — czysta funkcja: działa tylko na adresie bazowym strony.
 * Karta procedury ma pierwszeństwo przed widokiem: link z powiadomienia (`?doc=ID&etap=N`) dotyczy KONKRETNEJ procedury.
 */
const stareAdresy = ({ reszta, parametry }) => {
    if (reszta) return null;
    const doc = Number(parametry.get("doc"));
    if (doc > 0) {
        const nowe = bez(parametry, ["widok", "sekcja", "zakladka"]);
        const etap = NUMER_ETAPU[String(parametry.get("etap") || "").toLowerCase()];
        if (etap) nowe.set("etap", etap); else nowe.delete("etap");
        if (parametry.get("zakladka") === "przeglady") {
            if (!etap) nowe.set("etap", "4");
            if (!nowe.has("czesc")) nowe.set("czesc", "przeglady");
        }
        return { obszar: "procedury", zapytanie: nowe };
    }
    if (parametry.get("kreator") === "nowa") return { obszar: "procedury", zapytanie: bez(parametry, ["widok", "sekcja"]) };
    const widok = parametry.get("widok");
    if (widok === "procedury") return { obszar: "procedury", zapytanie: bez(parametry, ["widok", "sekcja"]) };
    if (widok === "braki") return { obszar: "czego-brakuje", zapytanie: bez(parametry, ["widok", "sekcja"]) };
    if (widok === "ustawienia") {
        const cel = SEKCJA_NA_OBSZAR[parametry.get("sekcja")] || { obszar: "ustawienia-uruchomienie" };
        const nowe = bez(parametry, ["widok", "sekcja"]);
        Object.entries(cel.dopisz || {}).forEach(([nazwa, wartosc]) => nowe.set(nazwa, wartosc));
        return { obszar: cel.obszar, zapytanie: nowe };
    }
    return null;
};

const procedury = {
    strona: "procedury-bezpieczenstwo",
    tytul: "Procedury i bezpieczeństwo",
    zdanie: "Prowadzi procedury spółek od napisania do potwierdzeń pracowników.",
    baza: { pl: "/admin/procedury-bezpieczenstwo", en: "/admin/procedury-bezpieczenstwo" },
    kluczStrony: KLUCZE_PROCEDUR,
    aktywna: true,

    grupy: [
        grupaStartu(),
        grupaPracy(),
        { id: GRUPA_BIBLIOTEK, etykieta: "Biblioteki i słowniki" },
        grupaUstawien(),
    ],

    obszary: [
        obszarStartu({
            kluczTrasy: "adminModule",
            komponent: () => import("../../pages/polityki/obszary/Start"),
            zdanie: "Prowadzi procedury spółek od napisania do potwierdzeń pracowników.",
            ikona: FiList,
            slowa: ["do załatwienia", "czeka na mnie", "mój krok", "start"],
        }),

        // ── Praca ──────────────────────────────────────────────────────────────────────────────
        obszar({
            id: "procedury", grupa: GRUPA_PRACY, segment: { pl: "procedury", en: "procedures" }, kluczTrasy: "adminProcProcedury",
            komponent: () => import("../../pages/polityki/obszary/Procedury"),
            etykieta: "Procedury", zdanie: "Tu znajdujesz procedurę i jej następny krok.",
            ikona: FiLayers, licznik: true,
            widoki: [
                { id: "czekaja-na-mnie", etykieta: "Czekają na mnie", licznik: true },
                { id: "wszystkie", etykieta: "Wszystkie" },
                { id: "wycofane", etykieta: "Wycofane" },
            ],
            filtry: ["filtrEtap", "spolka", "dzial", "obszar", "wlasciciel"],
            obiekty: [{
                parametr: "doc", rodzaj: "karta", nazwa: "procedura", dopelniacz: "procedury",
                dodatkowe: ["etap"],
                grupyCzesci: GRUPY_CZESCI_PROCEDURY,
                czesci: GRUPY_CZESCI_PROCEDURY.flatMap((g) => g.czesci),
                pokaz: {
                    adresaci: ["czynne", "wycofane"],
                    potwierdzenia: ["wszyscy", "spoznieni", "spoza-listy", "uniewaznione"],
                },
            }],
            kreatory: [{ nazwa: "nowa", tytul: "Nowa procedura", kroki: 3 }],
            pasek: { szukajka: "Szukaj procedury…", akcjaGlowna: "Nowa procedura", wiecej: ["Pobierz rejestr (XLSX)"] },
            slowa: ["procedura", "polityka", "rejestr", "etap", "przygotowanie", "zatwierdzenie", "publikacja", "pilnowanie", "okno procedury"],
        }),
        obszar({
            id: "czego-brakuje", grupa: GRUPA_PRACY, segment: { pl: "czego-brakuje", en: "gaps" }, kluczTrasy: "adminProcBraki",
            komponent: () => import("../../pages/polityki/obszary/CzegoBrakuje"),
            etykieta: "Czego brakuje", zdanie: "Tu widzisz, których wymaganych procedur brakuje w której spółce.",
            ikona: FiGrid, wzor: "macierz",
            widoki: [
                { id: "ustawa", etykieta: "Ustawa NIS2" },
                { id: "normy", etykieta: "Normy ISO" },
                { id: "wlasne", etykieta: "Własne" },
            ],
            filtry: ["braki"],
            slowa: ["braki", "macierz", "luki", "NIS2", "ISO", "wpis do wykazu KSC"],
        }),
        obszar({
            id: "stan-spolek", grupa: GRUPA_PRACY, segment: { pl: "stan-spolek", en: "companies-status" }, kluczTrasy: "adminProcStanSpolek",
            komponent: () => import("../../pages/polityki/obszary/StanSpolek"),
            etykieta: "Stan spółek", zdanie: "Tu porównujesz spółki: ile procedur obowiązuje i co jest po terminie.",
            ikona: FiBriefcase,
            slowa: ["pokrycie spółek", "porównanie spółek", "przeglądy po terminie", "kontrole po terminie"],
        }),
        obszar({
            id: "przeliczenie", grupa: GRUPA_PRACY, segment: { pl: "przeliczenie", en: "recalculation" }, kluczTrasy: "adminProcPrzeliczenie",
            komponent: () => import("../../pages/polityki/obszary/Przeliczenie"),
            etykieta: "Przeliczenie adresatów", zdanie: "Tu przeliczasz, kto musi potwierdzić opublikowane procedury.",
            ikona: FiRefreshCw, wzor: "jedna karta",
            slowa: ["przelicz teraz", "adresaci", "kto musi potwierdzić", "uzgodnienie"],
        }),

        // ── Biblioteki i słowniki ──────────────────────────────────────────────────────────────
        obszar({
            id: "wzory", grupa: GRUPA_BIBLIOTEK, segment: { pl: "wzory", en: "templates" }, kluczTrasy: "adminProcWzory",
            komponent: () => import("../../pages/polityki/obszary/Wzory"),
            etykieta: "Wzory procedur", zdanie: "Tu wybierasz gotowy wzór procedury.",
            ikona: FiCopy,
            filtry: ["kategoria", "obszar"],
            obiekty: [{
                parametr: "wzor", rodzaj: "karta", nazwa: "wzór procedury", dopelniacz: "wzoru procedury",
                czesci: ["opis", "przebieg", "wydruk"],
                pokaz: { przebieg: ["lista", "diagram"] },
            }],
            pasek: { szukajka: "Szukaj wzoru…" },
            slowa: ["wzór", "biblioteka wzorów", "gotowy wzór", "szablon"],
        }),
        obszar({
            id: "materialy", grupa: GRUPA_BIBLIOTEK, segment: { pl: "materialy", en: "materials" }, kluczTrasy: "adminProcMaterialy",
            komponent: () => import("../../pages/polityki/obszary/Materialy"),
            etykieta: "Materiały", zdanie: "Tu znajdujesz plik wgrany do którejś procedury.",
            ikona: FiImage, wzor: "siatka kart",
            filtry: ["rodzaj", "spolka", "obszar"],
            obiekty: [{ parametr: "material", rodzaj: "podglad", nazwa: "materiał", dopelniacz: "materiału" }],
            pasek: { szukajka: "Szukaj materiału…" },
            slowa: ["biblioteka materiałów", "pliki", "skany", "zrzuty", "multimedia"],
        }),
        obszar({
            id: "obszary", grupa: GRUPA_BIBLIOTEK, segment: { pl: "obszary", en: "areas" }, kluczTrasy: "adminProcObszary",
            komponent: () => import("../../pages/polityki/obszary/Obszary"),
            etykieta: "Obszary", zdanie: "Tu prowadzisz słownik tego, czego dotyczą procedury, i widzisz luki.",
            ikona: FiTag, wzor: "S2 + S7",
            widoki: [
                { id: "wszystkie", etykieta: "Wszystkie" },
                { id: "z-lukami", etykieta: "Z lukami", licznik: true },
                { id: "wlasne", etykieta: "Własne" },
            ],
            filtry: ["wylaczone"],
            obiekty: [{ parametr: "pozycja", rodzaj: "szuflada", nazwa: "obszar procedur", dopelniacz: "obszaru procedur" }],
            pasek: { szukajka: "Szukaj obszaru…", akcjaGlowna: "Dodaj obszar" },
            slowa: ["obszary własne", "słownik obszarów", "ustawa", "norma", "luki"],
        }),
        obszar({
            id: "stanowiska", grupa: GRUPA_BIBLIOTEK, segment: { pl: "stanowiska", en: "positions" }, kluczTrasy: "adminProcStanowiska",
            komponent: () => import("../../pages/polityki/obszary/Stanowiska"),
            etykieta: "Stanowiska", zdanie: "Tu sprawdzasz, kogo obejmą reguły potwierdzeń.",
            ikona: FiUsers, klucz: KLUCZ_RAPORTU_STANOWISK, wzor: "S2 + S7",
            obiekty: [{ parametr: "stanowisko", rodzaj: "szuflada", nazwa: "stanowisko", dopelniacz: "stanowiska" }],
            pasek: { szukajka: "Szukaj stanowiska…" },
            slowa: ["raport stanowisk", "stanowiska i ich różne nazwy", "kategoria pracy", "do poprawienia"],
        }),
        obszar({
            id: "nazwy-stanowisk", grupa: GRUPA_BIBLIOTEK, segment: { pl: "nazwy-stanowisk", en: "position-aliases" }, kluczTrasy: "adminProcNazwyStanowisk",
            komponent: () => import("../../pages/polityki/obszary/NazwyStanowisk"),
            etykieta: "Różne nazwy stanowisk", zdanie: "Tu uznajesz dwie pisownie za to samo stanowisko.",
            ikona: FiShuffle, klucz: KLUCZ_NAZW_STANOWISK, wzor: "S2 + S7",
            widoki: [
                { id: "obowiazujace", etykieta: "Obowiązujące" },
                { id: "historia", etykieta: "Historia" },
            ],
            obiekty: [{ parametr: "pisownia", rodzaj: "szuflada", nazwa: "inna nazwa stanowiska", dopelniacz: "innej nazwy stanowiska" }],
            pasek: { akcjaGlowna: "Dodaj inną nazwę" },
            slowa: ["aliasy stanowisk", "inna pisownia", "sklejanie nazw"],
        }),

        // ── Ustawienia ─────────────────────────────────────────────────────────────────────────
        obszar({
            id: "ustawienia-uruchomienie", grupa: GRUPA_USTAWIEN, segment: { pl: "uruchomienie", en: "setup" },
            kluczTrasy: "adminProcUstUruchomienie",
            komponent: () => import("../../pages/polityki/obszary/UstUruchomienie"),
            etykieta: "Uruchomienie", zdanie: "Tu widzisz, co zostało do zrobienia, żeby procedury zaczęły działać.",
            ikona: FiPlayCircle, klucz: KLUCZE_USTAWIEN, wzor: "S4",
            slowa: ["kroki", "kto gdzie pracuje w module", "kto co robi", "klucze"],
        }),
        obszar({
            id: "ustawienia-powiadomienia", grupa: GRUPA_USTAWIEN, segment: { pl: "powiadomienia", en: "notifications" },
            kluczTrasy: "adminProcUstPowiadomienia",
            komponent: () => import("../../pages/polityki/obszary/UstPowiadomienia"),
            etykieta: "Powiadomienia pracowników", zdanie: "Tu decydujesz, czy pracownicy dostają procedury do potwierdzenia.",
            ikona: FiBell, klucz: KLUCZE_USTAWIEN, wzor: "jedna decyzja",
            slowa: ["tryb próbny", "tryb cichy", "tryb pełny", "automat", "włącz powiadomienia"],
        }),
        obszar({
            id: "ustawienia-potwierdzenia", grupa: GRUPA_USTAWIEN, segment: { pl: "potwierdzenia", en: "acknowledgements" },
            kluczTrasy: "adminProcUstPotwierdzenia",
            komponent: () => import("../../pages/polityki/obszary/UstPotwierdzenia"),
            etykieta: "Potwierdzenia i zaległości", zdanie: "Tu ustawiasz terminy potwierdzeń, przypomnienia i powiadamianie przełożonych.",
            ikona: FiCheckCircle, klucz: KLUCZE_USTAWIEN, wzor: "S3",
            pasek: { akcjaGlowna: "Zapisz zmiany (2)" },
            slowa: ["potwierdzenia i przypomnienia", "zaległości", "eskalacja", "przełożony", "reguła dział"],
        }),
        obszar({
            id: "ustawienia-zarzad", grupa: GRUPA_USTAWIEN, segment: { pl: "zarzad", en: "board" },
            kluczTrasy: "adminProcUstZarzad",
            komponent: () => import("../../pages/polityki/obszary/UstZarzad"),
            /* [poprawka po przeglądzie 2026-10-06] Etykieta skrócona z „Zatwierdzanie przez zarząd” (tabela 4.1 projektu):
               sześć etykiet rzędu „Ustawienia” dawało 70.15rem przy twardym limicie 70rem (zasada Z-02c) — rząd miejsc
               zawijałby się na ekranie 1366 × 768. Teraz 68rem. Dawna nazwa zostaje w słowach szukania i w ściądze. */
            etykieta: "Zatwierdzanie zarządu", zdanie: "Tu ustawiasz, ilu głosów zarządu potrzeba do zatwierdzenia procedury.",
            ikona: FiAward, klucz: KLUCZE_USTAWIEN, wzor: "S3",
            pasek: { akcjaGlowna: "Zapisz zmiany (2)" },
            slowa: ["zatwierdzanie przez zarząd", "głosowanie", "skład zarządu", "jeden głos", "wszystkie głosy"],
        }),
        obszar({
            id: "ustawienia-spolki", grupa: GRUPA_USTAWIEN, segment: { pl: "spolki", en: "companies" },
            kluczTrasy: "adminProcUstSpolki",
            komponent: () => import("../../pages/polityki/obszary/UstSpolki"),
            etykieta: "Spółki objęte ustawą", zdanie: "Tu wskazujesz, które spółki podlegają ustawie o cyberbezpieczeństwie.",
            ikona: FiShield, klucz: KLUCZE_USTAWIEN, wzor: "S3",
            slowa: ["objęta ustawą", "NIS2", "w Wykazie KSC", "poza wykazem"],
        }),
        obszar({
            id: "ustawienia-publikacja", grupa: GRUPA_USTAWIEN, segment: { pl: "publikacja", en: "publication" },
            kluczTrasy: "adminProcUstPublikacja",
            komponent: () => import("../../pages/polityki/obszary/UstPublikacja"),
            etykieta: "Publikacja i kontrole", zdanie: "Tu ustawiasz ogłaszanie publikacji, archiwum i przypomnienia o kontrolach.",
            ikona: FiSend, klucz: KLUCZE_USTAWIEN, wzor: "S3",
            pasek: { akcjaGlowna: "Zapisz zmiany (2)" },
            slowa: ["publikacja i archiwum", "kontrole przestrzegania", "ogłoszenie", "zbliża się"],
        }),
    ],

    trasyObiektow: [],

    /** Źródła listy „Do załatwienia” — cztery odczyty (`pages/polityki/zrodlaStartu.js`), zgodne z planem niżej (4.2 projektu). */
    zrodlaStartu: ZRODLA_STARTU_PROCEDUR,
    sufitOdczytowStartu: 4,
    zrodlaStartuPlan: [
        { id: "lista", odczyt: "GET /polityki", klucz: KLUCZE_PROCEDUR },
        { id: "macierz", odczyt: "GET /polityki/obszary/macierz", klucz: KLUCZE_PROCEDUR },
        { id: "automat", odczyt: "GET /polityki/automat", klucz: KLUCZE_PROCEDUR },
        { id: "ustawienia", odczyt: "GET /polityki/ustawienia", klucz: KLUCZE_USTAWIEN },
    ],

    stareAdresy,
    stareSlugi: {},

    slownikPojec: [
        { slowo: "procedura", definicja: "Dokument spółki, który mówi, jak ma być robiona dana rzecz, i który pracownicy potwierdzają." },
        { slowo: "etap", definicja: "Jeden z czterech odcinków drogi procedury: przygotowanie, zatwierdzenie, publikacja i pilnowanie." },
        { slowo: "wersja", definicja: "Zamrożona treść procedury wysłana do zatwierdzenia, której nie da się już zmienić." },
        { slowo: "recenzent", definicja: "Osoba, która sprawdza treść procedury, zanim trafi ona pod głosowanie zarządu." },
        { slowo: "głosowanie zarządu", definicja: "Decyzja członków zarządu spółki, bez której procedury nie da się opublikować." },
        { slowo: "publikacja", definicja: "Chwila, od której procedura obowiązuje, a wskazani pracownicy dostają ją do potwierdzenia." },
        { slowo: "kto musi potwierdzić", definicja: "Reguły wskazujące osoby zobowiązane do potwierdzenia, że zapoznały się z procedurą." },
        { slowo: "przeliczenie", definicja: "Ponowne ustalenie listy osób, które muszą potwierdzić procedurę, według aktualnych reguł i stanowisk." },
        { slowo: "przegląd treści", definicja: "Okresowe sprawdzenie, czy treść procedury jest nadal aktualna." },
        { slowo: "kontrola", definicja: "Okresowe sprawdzenie, czy ludzie przestrzegają procedury, z zapisem wyniku i dowodów." },
        { slowo: "obszar", definicja: "Temat, którego dotyczy procedura: wymagany przez ustawę, wzięty z normy albo własny temat spółki." },
        { slowo: "wzór", definicja: "Gotowa treść procedury z biblioteki, od której można zacząć zamiast pisać od pustej kartki." },
        { slowo: "materiał", definicja: "Plik wgrany do procedury, na przykład skan, zdjęcie albo dokument źródłowy." },
        { slowo: "tryb próbny", definicja: "System liczy, kto powinien potwierdzić, ale nie zapisuje obowiązków i nic nie wysyła do pracowników." },
        { slowo: "tryb cichy", definicja: "Obowiązki potwierdzenia się zapisują, ale pracownicy nie dostają o nich powiadomień." },
        { slowo: "tryb pełny", definicja: "Obowiązki potwierdzenia się zapisują, a pracownicy dostają powiadomienia i przypomnienia." },
        { slowo: "kod kontrolny", definicja: "Ciąg znaków wyliczony z treści wersji, po którym widać, że nikt jej potem nie zmienił." },
    ],

    /** „Kto co robi” (dawna sekcja ustawień „Kto gdzie pracuje w module” — F060): siedem ról i ich miejsca. Klucze i miejsca, które
     *  otwierają, słowniczek liczy sam z rejestru. */
    ktoCoRobi: [
        { rola: "Redakcja, IT i compliance", co: "Menu ZGODNOŚĆ → „Procedury i bezpieczeństwo”: piszą i prowadzą procedury od pierwszej wersji do publikacji." },
        { rola: "Kierownik działu", co: "Mój Dział → „Procedury”: procedury działu i przypisanie ich swoim ludziom do potwierdzenia." },
        { rola: "Członek działu", co: "Dokumenty → „Do potwierdzenia” → „Procedury mojego działu”: lista rzeczy do sprawdzenia i „Przygotuj procedurę działu”." },
        { rola: "Właściciel procedury bez klucza", co: "Otwiera swoją procedurę z powiadomienia — karta procedury otwiera się wprost z linku." },
        { rola: "Recenzent i zarząd", co: "Dokumenty → „Do decyzji”: sprawdzenie treści i głosowanie nad procedurą." },
        { rola: "Pracownik", co: "Dokumenty → „Do potwierdzenia” (i kafel na Pulpicie): przeczytanie i potwierdzenie procedur, które go dotyczą." },
        { rola: "Pełnomocnik KSC", co: "Menu ZGODNOŚĆ → „Wykaz KSC”: pilnowanie, żeby zmiany danych spółki trafiły do wykazu w 14 dni." },
    ],

    gdzieCoBylo: [
        { bylo: "Zakładka „Procedury” (cztery tabele po etapach)", jest: "Praca → Procedury — jedna tabela z kolumną „Etap”", obszar: "procedury" },
        { bylo: "Zakładka „Czego brakuje”", jest: "Praca → Czego brakuje", obszar: "czego-brakuje" },
        { bylo: "Pokrycie spółek", jest: "Praca → Stan spółek", obszar: "stan-spolek" },
        { bylo: "„Przelicz teraz” w oknie procedury (dla wszystkich procedur)", jest: "Praca → Przeliczenie adresatów", obszar: "przeliczenie" },
        { bylo: "Wzory w oknie „Nowa procedura”", jest: "Biblioteki i słowniki → Wzory procedur", obszar: "wzory" },
        { bylo: "„Biblioteka materiałów” (przycisk w nagłówku)", jest: "Biblioteki i słowniki → Materiały", obszar: "materialy" },
        { bylo: "Ustawienia → Obszary własne", jest: "Biblioteki i słowniki → Obszary", obszar: "obszary" },
        { bylo: "Ustawienia → Stanowiska i ich różne nazwy", jest: "Biblioteki i słowniki → Stanowiska oraz Różne nazwy stanowisk", obszar: "stanowiska" },
        { bylo: "Żółta karta trybu próbnego nad listą", jest: "Do załatwienia → blok „Stan” oraz Ustawienia → Powiadomienia pracowników", obszar: "ustawienia-powiadomienia" },
        { bylo: "Ustawienia → Potwierdzenia i przypomnienia; Zaległości", jest: "Ustawienia → Potwierdzenia i zaległości", obszar: "ustawienia-potwierdzenia" },
        { bylo: "Ustawienia → Zatwierdzanie przez zarząd", jest: "Ustawienia → Zatwierdzanie zarządu", obszar: "ustawienia-zarzad" },
        { bylo: "Ustawienia → Spółki objęte ustawą", jest: "Ustawienia → Spółki objęte ustawą", obszar: "ustawienia-spolki" },
        { bylo: "Ustawienia → Publikacja i archiwum; Kontrole przestrzegania", jest: "Ustawienia → Publikacja i kontrole", obszar: "ustawienia-publikacja" },
        { bylo: "Ustawienia → Kto gdzie pracuje w module", jest: "Więcej → Pojęcia → Kto co robi", obszar: "ustawienia-uruchomienie" },
        { bylo: "Okno procedury z dwiema belkami i panelem etapu", jest: "Karta procedury: jedna belka, etapy i części", obszar: "procedury" },
    ],

    stareNazwyMiejsc: [
        [/Procedury i bezpieczeństwo\s*(?:→|->)\s*Ustawienia\s*(?:→|->)\s*Stanowiska i ich różne nazwy/g, "Procedury i bezpieczeństwo → Biblioteki i słowniki → Stanowiska"],
        [/Procedury i bezpieczeństwo\s*(?:→|->)\s*Ustawienia\s*(?:→|->)\s*Obszary własne/g, "Procedury i bezpieczeństwo → Biblioteki i słowniki → Obszary"],
        [/Ustawienia\s*(?:→|->)\s*Zatwierdzanie przez zarząd/g, "Ustawienia → Zatwierdzanie zarządu"],
        [/Polityki i procedury/g, "Procedury i bezpieczeństwo"],
    ],
};

export default procedury;
